import { useState, useEffect, useContext } from "react";
import { UserContext } from "../../contexts/UserContext";
import { X, Copy } from "lucide-react"; // Import the X and Copy icons from lucide-react
import { toast } from "react-toastify"; // Import toast
import axios from "../../axios-config"; // Import axios

interface ProfileSettingsProps {
  profile: any;
  onClose: () => void;
}

export default function ProfileSettings({ profile, onClose }: ProfileSettingsProps) {
  const [fullName, setFullName] = useState(profile?.name || "");
  const [givenName, setGivenName] = useState(profile?.givenName || "");
  const [picture, setPicture] = useState(profile?.picture || "");
  const [email, setEmail] = useState(profile?.email || "");
  //const [phoneNumber, setPhoneNumber] = useState(profile?.phoneNumber || "");
  const [avatar, setAvatar] = useState(profile?.avatar || "");
  
  const [cssOption, setCssOption] = useState<string>("default");
  const [dropdownCss, setDropdownCss] = useState<string>("dark");
  const [customCssText, setCustomCssText] = useState<string>("");
  const [themeOptions, setThemeOptions] = useState<string[]>([]);

  // Remove the copyTooltip state as we'll use toast instead

  // Set radio state based on profile.theme or profile.customCss
  useEffect(() => {
    // Preserve customCss even if theme changes
    if (profile) {
      setCustomCssText(profile.customCss || "");
      if (profile.theme) {
        if (profile.theme === "custom") {
          setCssOption("custom");
        } else if (profile.theme === "Index") {
          setCssOption("default");
        } else {
          setCssOption("dropdown");
          setDropdownCss(profile.theme);
        }
      } else {
        setCssOption("default");
      }
    }
  }, [profile]);

  const { refreshProfile } = useContext(UserContext);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    // Determine effective CSS based on current selection
    const effectiveCss =
      cssOption === "dropdown" ? dropdownCss : cssOption === "custom" ? customCssText : "Index";
    let linkEl: HTMLLinkElement | null = null;
    let styleEl: HTMLStyleElement | null = null;
    // Clean up any previous elements is handled in cleanup below
    if (effectiveCss) {
      // If effectiveCss contains no whitespace, assume built-in stylesheet
      if (!/\s/.test(effectiveCss)) {
        linkEl = document.createElement("link");
        linkEl.rel = "stylesheet";
        let fileName = "./styles/" + effectiveCss;
        if (!fileName.endsWith(".css")) fileName += ".css";
        if (process.env.NODE_ENV === "development") {
          linkEl.href = `/src/${fileName}`;
        } else {
          linkEl.href = import.meta.env.BASE_URL + fileName;
        }
        document.head.appendChild(linkEl);
      } else {
        // Otherwise add a style element with custom CSS
        styleEl = document.createElement("style");
        styleEl.textContent = effectiveCss;
        document.head.appendChild(styleEl);
      }
    }
    return () => {
      if (linkEl && linkEl.parentNode) linkEl.parentNode.removeChild(linkEl);
      if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, [cssOption, dropdownCss, customCssText]);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const themeFiles = import.meta.glob("/src/styles/*.css");
        const themes = Object.keys(themeFiles)
          .map((filePath) => filePath.split("/").pop()?.replace(".css", ""))
          .filter((theme): theme is string => !!theme && theme !== "index"); // changed filter to ensure only strings
        setThemeOptions(themes);
      } catch (error) {
        console.error("Error fetching themes:", error);
        toast.error("Failed to load themes");
      }
    };

    fetchThemes();
  }, []);

  const handleCopyCSS = async () => {
    try {
      // Fetch the CSS content based on the selected theme
      const fileName = `./styles/${dropdownCss}.css`;
      const cssPath = process.env.NODE_ENV === "development" 
        ? `/src/${fileName}` 
        : import.meta.env.BASE_URL + fileName;
      
      const response = await fetch(cssPath);
      if (!response.ok) throw new Error("Failed to fetch CSS");
      
      const cssContent = await response.text();
      await navigator.clipboard.writeText(cssContent);
      
      // Show success toast instead of tooltip
      toast.success("CSS copied to clipboard!");
    } catch (error) {
      console.error("Error copying CSS:", error);
      // Show error toast
      toast.error("Failed to copy CSS");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const themeValue = cssOption === "dropdown" 
      ? dropdownCss 
      : cssOption === "default" 
        ? "Index" 
        : "custom";
    const updatedProfile = { 
      FullName: fullName, 
      GivenName: givenName, 
      Picture: picture, 
      Email: email, 
      Avatar: avatar, 
      Theme: themeValue,
      CustomCss: customCssText 
    };
    try {
      const res = await axios.put("/api/profile", updatedProfile, {
        headers: { "Content-Type": "application/json" }
      });
      if (res.status >= 200 && res.status < 300) {
        await refreshProfile();
        onClose();
      } else {
        alert("Failed to update profile");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      alert("Failed to update profile");
    }
  };

  const applyTheme = (theme: string) => {
    const effectiveCss = theme;
    let linkEl: HTMLLinkElement | null = null;
    let styleEl: HTMLStyleElement | null = null;

    // Remove any existing theme styles
    const existingLinks = document.querySelectorAll("link[rel='stylesheet']");
    existingLinks.forEach((link: any) => {
      const htmlLink = link as HTMLLinkElement; // cast inside the callback
      if (htmlLink.href.includes("/src/styles/")) {
        htmlLink.parentNode?.removeChild(htmlLink);
      }
    });

    const existingStyles = document.querySelectorAll("style");
    existingStyles.forEach((style) => {
      if (style.textContent?.includes("/* Custom CSS */")) {
        style.parentNode?.removeChild(style);
      }
    });

    if (effectiveCss) {
      if (!/\s/.test(effectiveCss)) {
        linkEl = document.createElement("link");
        linkEl.rel = "stylesheet";
        linkEl.href = `/src/styles/${effectiveCss}.css`;
        document.head.appendChild(linkEl);
      } else {
        styleEl = document.createElement("style");
        styleEl.textContent = `/* Custom CSS */\n${effectiveCss}`;
        document.head.appendChild(styleEl);
      }
    }
  };

  useEffect(() => {
    applyTheme(cssOption === "dropdown" ? dropdownCss : cssOption === "custom" ? customCssText : "Index");
  }, [cssOption, dropdownCss, customCssText]);

  return (
    <div className="h-full p-6 relative">
      <button onClick={onClose} title="Close" className="absolute top-2 right-2 p-1">
        <X size={20} />
      </button>
      <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
      <form onSubmit={handleSubmit}>
        <label>
          Full Name:
          <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className="w-full border p-2 my-1" />
        </label>
        <label>
          Given Name:
          <input type="text" value={givenName} onChange={e => setGivenName(e.target.value)} className="w-full border p-2 my-1" />
        </label>
        <label hidden>
          Picture URL:
          <input type="text" value={picture} onChange={e => setPicture(e.target.value)} className="w-full border p-2 my-1" />
        </label>
        <label>
          Email:
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border p-2 my-1" />
        </label>
        {/* <label>
          Phone Number:
          <input type="text" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className="w-full border p-2 my-1" />
        </label> */}
        <label hidden>
          Avatar:
          <input type="text" value={avatar} onChange={e => setAvatar(e.target.value)} className="w-full border p-2 my-1" />
        </label>
        <label>
          Look and Feel:
        </label>
        <div className="radio-group-horizontal">
          <label className="flex items-center">
            <input
              type="radio"
              name="cssOption"
              value="default"
              checked={cssOption === "default"}
              onChange={() => setCssOption("default")}
            />
            <span className="ml-2">Default</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="cssOption"
              value="dropdown"
              checked={cssOption === "dropdown"}
              onChange={() => setCssOption("dropdown")}
            />
            <span className="ml-2">Themes</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="cssOption"
              value="custom"
              checked={cssOption === "custom"}
              onChange={() => setCssOption("custom")}
            />
            <span className="ml-2">Custom</span>
          </label>
        </div>
        {cssOption === "dropdown" && (
          <div className="flex items-center gap-2">
            <button 
              type="button"
              onClick={handleCopyCSS}
              className="p-2 border rounded hover:bg-gray-100 transition-colors"
              title="Copy CSS to clipboard"
            >
              <Copy size={16} />
            </button>
            <select
              value={dropdownCss}
              onChange={e => {
                setDropdownCss(e.target.value);
                applyTheme(e.target.value);
              }}
              className="flex-1 border p-2 mt-1"
            >
              {themeOptions.map((theme) => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
            </select>
          </div>
        )}
        {cssOption === "custom" && (
          <textarea
            value={customCssText}
            onChange={e => setCustomCssText(e.target.value)}
            className="w-full border p-2 mt-1"
            rows={3}
          />
        )}
        <button type="submit" className="mt-4 bg-blue-600 text-white px-4 py-2 rounded">
          Save Changes
        </button>
        <button type="button" onClick={onClose} className="mt-4 ml-2 bg-gray-300 text-gray-800 px-4 py-2 rounded">
          Cancel
        </button>
      </form>
    </div>
  );
}
